import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../api/axios';

// Payriff ödənişindən sonra geri dönüş səhifəsi (/rezerv-netice?t=&req=).
// Payriff callbackUrl bura yönləndirir → statusu backend-dən təsdiqləyirik.
// Ödənilibsə: təsdiq + "şəklini çəkib WhatsApp/Instagram göndər" mesajı.

const BRAND = '#0090bb';
const money = (v) => `${Number(v || 0).toFixed(0)} ₼`;
const fmtDate = (s) => String(s || '').slice(0, 10);
// AZ (994…) → lokal 0XXXXXXXXX; xarici → +<rəqəmlər>
const fmtPhone = (raw) => {
    const d = String(raw || '').replace(/\D/g, '');
    if (!d) return '';
    return d.startsWith('994') ? '0' + d.slice(3) : '+' + d;
};

export default function BookingResult() {
    const [params] = useSearchParams();
    const token = params.get('t') || '';
    const reqId = params.get('req') || '';
    const isBanquet = params.get('type') === 'banket';   // banket sifarişi vs ev rezervasiyası

    const [state, setState] = useState('checking'); // checking | paid | unpaid | error
    const [info, setInfo] = useState(null);
    const tries = useRef(0);

    const check = useCallback(async () => {
        try {
            const endpoint = isBanquet ? '/public-booking/banquet-status' : '/public-booking/payment-status';
            const res = await api.get(endpoint, { params: { token, req: reqId } });
            setInfo(res.data);
            if (res.data?.paid) { setState('paid'); return true; }
            return false;
        } catch {
            setState('error');
            return true; // dayan
        }
    }, [token, reqId, isBanquet]);

    useEffect(() => {
        if (!token || !reqId) { setState('error'); return; }
        let stopped = false;
        const run = async () => {
            const done = await check();
            if (done || stopped) return;
            // Callback bəzən statusdan bir az əvvəl gəlir — bir neçə dəfə yoxla
            if (tries.current < 5) {
                tries.current += 1;
                setTimeout(run, 2000);
            } else {
                setState('unpaid');
            }
        };
        run();
        return () => { stopped = true; };
    }, [token, reqId, check]);

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4"
             style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
            <div className="w-full max-w-md rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">

                {state === 'checking' && (
                    <div className="py-10 text-center">
                        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-gray-200"
                             style={{ borderTopColor: BRAND }} />
                        <p className="mt-4 text-sm font-semibold text-gray-500">Ödəniş təsdiqlənir...</p>
                    </div>
                )}

                {state === 'paid' && (
                    <div className="text-center">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-4xl">✅</div>
                        <h1 className="mt-4 text-xl font-black text-gray-900">{isBanquet ? 'Sifarişiniz tamamlandı' : 'Rezervasiyanız tamamlandı'}</h1>

                        {info && (
                            <div className="mt-4 rounded-2xl border border-gray-100 bg-gray-50 p-4 text-left text-sm">
                                {info.banquet_name && (
                                    <div className="flex justify-between py-1">
                                        <span className="text-gray-500">Banket</span>
                                        <span className="font-bold text-gray-800">{info.banquet_name}</span>
                                    </div>
                                )}
                                {info.house_name && (
                                    <div className="flex justify-between py-1">
                                        <span className="text-gray-500">Ev</span>
                                        <span className="font-bold text-gray-800">{String(info.house_name).split(' (')[0]}</span>
                                    </div>
                                )}
                                {info.guest_name && (
                                    <div className="flex justify-between py-1">
                                        <span className="text-gray-500">Ad, Soyad</span>
                                        <span className="font-bold text-gray-800">{info.guest_name}</span>
                                    </div>
                                )}
                                {info.phone && (
                                    <div className="flex justify-between py-1">
                                        <span className="text-gray-500">Əlaqə</span>
                                        <span className="font-bold text-gray-800" dir="ltr">{fmtPhone(info.phone)}</span>
                                    </div>
                                )}
                                {!isBanquet && info.check_in && (
                                    <div className="flex justify-between py-1">
                                        <span className="text-gray-500">Tarix</span>
                                        <span className="font-bold text-gray-800">{fmtDate(info.check_in)} → {fmtDate(info.check_out)} ({info.nights} gecə)</span>
                                    </div>
                                )}
                                {isBanquet && Number(info.guests) > 0 && (
                                    <div className="flex justify-between py-1">
                                        <span className="text-gray-500">Nəfər</span>
                                        <span className="font-bold text-gray-800">{info.guests} nəfər</span>
                                    </div>
                                )}
                                {!isBanquet && (Number(info.adults) > 0 || Number(info.children) > 0) && (
                                    <div className="flex justify-between py-1">
                                        <span className="text-gray-500">Nəfər</span>
                                        <span className="font-bold text-gray-800">
                                            {info.adults} böyük{Number(info.children) > 0 ? ` · ${info.children} uşaq` : ''}
                                        </span>
                                    </div>
                                )}
                                <div className="mt-1 flex justify-between border-t border-gray-200 pt-2">
                                    <span className="font-semibold text-emerald-700">Ödənilən beh</span>
                                    <span className="font-black text-emerald-700">{money(info.deposit)}</span>
                                </div>
                                {Number(info.total) > 0 && (
                                    <div className="flex justify-between py-1 text-xs text-gray-400">
                                        <span>Cəmi rezervasiya</span>
                                        <span>{money(info.total)} (qalığı yerində)</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="mt-4 rounded-2xl border-2 border-dashed p-4 text-sm font-bold leading-relaxed"
                             style={{ borderColor: BRAND, color: '#075985' }}>
                            📸 Zəhmət olmasa bu ekranın şəklini çəkib <b>WhatsApp</b> və ya <b>Instagram</b> əlaqə saxladığınız yerə göndərin.
                        </div>

                        {token && (
                            <a href={`${isBanquet ? "/banket" : "/evler"}?t=${encodeURIComponent(token)}`}
                               className="mt-4 inline-block rounded-xl px-5 py-2.5 text-sm font-bold text-white"
                               style={{ background: `linear-gradient(135deg, ${BRAND}, #006f91)` }}>
                                {isBanquet ? "← Banketlərə qayıt" : "← Evlərə qayıt"}
                            </a>
                        )}
                    </div>
                )}

                {(state === 'unpaid') && (
                    <div className="text-center">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 text-4xl">⏳</div>
                        <h1 className="mt-4 text-lg font-black text-gray-900">Ödəniş təsdiqlənmədi</h1>
                        <p className="mt-2 text-sm text-gray-500">
                            Ödəniş hələ təsdiqlənməyib. Əgər məbləğ çıxılıbsa, bir neçə dəqiqə sonra bu səhifəni yeniləyin
                            və ya bizimlə əlaqə saxlayın.
                        </p>
                        <button type="button" onClick={() => { tries.current = 0; setState('checking'); }}
                                className="mt-4 rounded-xl px-5 py-2.5 text-sm font-bold text-white"
                                style={{ background: `linear-gradient(135deg, ${BRAND}, #006f91)` }}>
                            Yenidən yoxla
                        </button>
                    </div>
                )}

                {state === 'error' && (
                    <div className="text-center">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-rose-100 text-4xl">⚠️</div>
                        <h1 className="mt-4 text-lg font-black text-gray-900">Nəticə tapılmadı</h1>
                        <p className="mt-2 text-sm text-gray-500">Link yanlışdır və ya rezervasiya tapılmadı.</p>
                        {token && (
                            <a href={`${isBanquet ? "/banket" : "/evler"}?t=${encodeURIComponent(token)}`}
                               className="mt-4 inline-block rounded-xl px-5 py-2.5 text-sm font-bold text-white"
                               style={{ background: `linear-gradient(135deg, ${BRAND}, #006f91)` }}>
                                {isBanquet ? "← Banketlərə qayıt" : "← Evlərə qayıt"}
                            </a>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
